import { RotateCcw } from "lucide-react";
import React, { useEffect, useState, useRef } from "react";
import "./index.css";

interface AllocationTableProps {
  allocations: any;
  updateAllocation: (
    cartId: string,
    productItemId: string,
    portionSize: string,
    value: string,
    isUserInput?: boolean
  ) => void;
  getCartTotal: (cartId: string) => number;
  getProductTotal: (productItemId: string, portionSize: string) => number;
  getProductCombinations: (allocations: any) => any[];
  originalAllocations: any;
  resetToSuggested: (
    cartId: string,
    productItemId: string,
    portionSize: string
  ) => void;
  isLoading: boolean;
}

const AllocationTable: React.FC<AllocationTableProps> = ({
  allocations,
  updateAllocation,
  getCartTotal,
  getProductTotal,
  getProductCombinations,
  originalAllocations,
  resetToSuggested,
  isLoading,
}) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // Sync horizontal scrolling between header and body
  useEffect(() => {
    const bodyContainer = bodyScrollRef.current;
    const headerContainer = headerScrollRef.current;

    const handleBodyScroll = () => {
      if (bodyContainer && headerContainer) {
        headerContainer.scrollLeft = bodyContainer.scrollLeft;
      }
    };

    const handleHeaderScroll = () => {
      if (bodyContainer && headerContainer) {
        bodyContainer.scrollLeft = headerContainer.scrollLeft;
      }
    };

    if (bodyContainer) {
      bodyContainer.addEventListener("scroll", handleBodyScroll);
    }

    if (headerContainer) {
      headerContainer.addEventListener("scroll", handleHeaderScroll);
    }

    return () => {
      if (bodyContainer) {
        bodyContainer.removeEventListener("scroll", handleBodyScroll);
      }
      if (headerContainer) {
        headerContainer.removeEventListener("scroll", handleHeaderScroll);
      }
    };
  }, []);

  const productCombinations = getProductCombinations(allocations);
  const cartIds = Object.keys(allocations);

  const isValueModified = (
    cartId: string,
    productItemId: string,
    portionSize: string,
    currentValue: number
  ) => {
    const originalCart = originalAllocations[cartId];
    if (!originalCart) return false;

    const originalItem = originalCart.allocations.find(
      (a: any) =>
        a.productItemId === productItemId && a.portionSize === portionSize
    );

    return originalItem && originalItem.directIssues !== currentValue;
  };

  return (
    <div className={`allocation-table-container`}>
      {/* Header Container - Scrollable horizontally only */}
      <div className="allocation-table-header-container" ref={headerScrollRef}>
        <table className="allocation-table">
          <thead className="allocation-table-header">
            <tr>
              <th className="allocation-table-header-cell allocation-table-header-cell--fixed-left">
                <div className="allocation-table-product-header">
                  Product Combinations
                  <span className="allocation-table-product-count">
                    ({productCombinations.length} items)
                  </span>
                </div>
              </th>
              {cartIds.map((cartId) => {
                const cart = allocations[cartId];
                return (
                  <th
                    key={cartId}
                    className="allocation-table-header-cell allocation-table-header-cell--cart"
                  >
                    <div className="allocation-table-cart-header">
                      <div
                        className="allocation-table-cart-name"
                        title={cart.cartName}
                      >
                        {cart.abbreviation && cart.abbreviation !== ""
                          ? cart.abbreviation
                          : cart.cartName}
                      </div>
                      {/* <div className="allocation-table-cart-id">{cartId}</div> */}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
        </table>
      </div>

      {/* Body Container - Scrollable both horizontally and vertically */}
      <div className="allocation-table-body-container" ref={bodyScrollRef}>
        <table className="allocation-table">
          <tbody>
            {productCombinations.map((product, index) => (
              <tr
                key={`${product.productItemId}-${product.portionSize}`}
                className={
                  index % 2 === 0
                    ? "allocation-table-row--even"
                    : "allocation-table-row--odd"
                }
              >
                <td className="allocation-table-cell allocation-table-cell--fixed-left">
                  <div className="allocation-table-product-info">
                    <div
                      className="allocation-table-product-name"
                      title={product.displayName}
                    >
                      {product.abbreviation && product.abbreviation !== ""
                        ? product.abbreviation
                        : product.displayName}
                    </div>
                    <div className="allocation-table-portion-size">
                      <span className="allocation-table-portion-size-badge">
                        {product.portionSize}
                      </span>
                    </div>
                  </div>
                </td>
                {cartIds.map((cartId) => {
                  const cartAllocation = allocations[cartId];
                  const item = cartAllocation.allocations.find(
                    (a: any) =>
                      a.productItemId === product.productItemId &&
                      a.portionSize === product.portionSize
                  );

                  const isModified = isValueModified(
                    cartId,
                    product.productItemId,
                    product.portionSize,
                    item?.directIssues || 0
                  );

                  return (
                    <td
                      key={cartId}
                      className="allocation-table-cell allocation-table-cell--cart"
                    >
                      <div className="allocation-table-input-wrapper">
                        <div
                          className={`allocation-table-input-container ${
                            isModified ? "allocation-table-input--modified" : ""
                          } ${
                            isLoading ? "allocation-table-input--disabled" : ""
                          }`}
                        >
                          <input
                            type="number"
                            value={item?.directIssues || ""}
                            onChange={(e) =>
                              updateAllocation(
                                cartId,
                                product.productItemId,
                                product.portionSize,
                                e.target.value
                              )
                            }
                            className="allocation-table-input-field"
                            min="0"
                            disabled={isLoading}
                            placeholder="0"
                          />
                          {isModified && !isLoading && (
                            <button
                              onClick={() =>
                                resetToSuggested(
                                  cartId,
                                  product.productItemId,
                                  product.portionSize
                                )
                              }
                              className="allocation-table-reset-button"
                              title="Reset to original value"
                            >
                              <RotateCcw className="allocation-table-reset-icon" />
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}

            {/* Cart Totals Row */}
            <tr className="allocation-table-totals-row">
              <td className="allocation-table-cell allocation-table-cell--fixed-left allocation-table-totals-cell">
                <div className="allocation-table-totals-label">Cart Totals</div>
              </td>
              {cartIds.map((cartId) => {
                const total = getCartTotal(cartId);
                return (
                  <td
                    key={cartId}
                    className="allocation-table-cell allocation-table-cell--cart allocation-table-totals-cell"
                  >
                    <div
                      className={`allocation-table-cart-total ${
                        isLoading ? "allocation-table-cart-total--disabled" : ""
                      }`}
                    >
                      {total}
                    </div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllocationTable;
